import { useCallback, useRef, useState } from 'react'

const LG = '(min-width: 1024px)'

/**
 * De maandselectie achter een grafiek waarvan een kolom naar een uitsplitsing leidt.
 * Zonder scroll verandert er op een kort scherm iets buiten beeld en lijkt de tik niets
 * te doen; `panelAlwaysBelow` zegt of dat ook op een breed scherm zo is. Op het dashboard
 * neemt het paneel boven `lg` de kaart ernaast over en staat het dus al in beeld.
 */
export function useMonthSelection<P extends { month: string | number }>(
  points: P[] | undefined,
  { panelAlwaysBelow = false }: { panelAlwaysBelow?: boolean } = {},
) {
  type Month = P['month']
  const [month, setMonth] = useState<Month | null>(null)
  // Uitgeklapt hoort bij de kijker, niet bij de maand: wie eenmaal alle regels wil zien,
  // wil dat ook in de volgende maand. Daarom staat het hier en niet in het paneel, dat
  // bij elke maandwissel opnieuw wordt opgebouwd.
  const [expanded, setExpanded] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)

  const selectedPoint = month === null ? undefined : points?.find((p) => p.month === month)

  const selectMonth = useCallback((next: Month | null) => {
    setMonth(next)
    if (next === null || (!panelAlwaysBelow && window.matchMedia(LG).matches)) {
      return
    }
    // De globale reduced-motion-regel in index.css raakt CSS-animaties, niet
    // scroll-behavior; die afweging staat hier dus zelf.
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    requestAnimationFrame(() => {
      panelRef.current?.scrollIntoView({ block: 'nearest', behavior: smooth ? 'smooth' : 'auto' })
    })
  }, [panelAlwaysBelow])

  // Wijst de selectie nergens naar (data nog niet binnen), dan is er geen selectie.
  return { selectedMonth: selectedPoint ? month : null, selectedPoint, selectMonth, panelRef, expanded, setExpanded }
}
